import React, { useRef } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayIcon from '@mui/icons-material/PlayArrow';

import type {
  ScriptStep,
  UserInputMode,
} from '@shared/interfaces/assistantScripts';
import type { PollyLanguage } from '@shared/interfaces/polly';
import { languageFlags, languageLabels } from '../constants';
import { SectionLabelText } from '../AssistantScriptDialog.styled';
import {
  BaseLangLabel,
  BaseTextBox,
  DeleteStepButton,
  ReorderButtonsBox,
  ScriptTextArea,
  ScriptTextHintIcon,
  StepAccordion,
  StepAccordionDetails,
  StepAccordionSummary,
  StepContentBox,
  StepTitle,
} from './styled';
import AdvanceToNextStepOptions from '../AdvanceToNextStepOptions';
import QuestionarySection from '../QuestionarySection';
import StepPreviewButton from '../StepPreviewButton';
import UserInputControl from '../UserInputControl';

interface ScriptStepItemProps {
  step: ScriptStep;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  expanded: boolean;
  activeLanguage: PollyLanguage;
  effectiveBaseLanguage: PollyLanguage;
  stepUserInputMode: UserInputMode;
  audioPlaybackKey: 'test' | number | null;
  isAudioPlaying: boolean;
  userFirstName?: string;
  mapName?: string;
  onToggleExpanded: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onUpdateStep: (data: Partial<ScriptStep>) => void;
  onUpdateStepTranslation: (lang: PollyLanguage, value: string) => void;
  onUserInputModeChange: (mode: UserInputMode) => void;
  onRemove: () => void;
  onPlay: (text: string, index: number) => void;
  onStop: () => void;
}

const ScriptStepItem = ({
  step,
  index,
  isFirst,
  isLast,
  expanded,
  activeLanguage,
  effectiveBaseLanguage,
  stepUserInputMode,
  audioPlaybackKey,
  isAudioPlaying,
  userFirstName,
  mapName,
  onToggleExpanded,
  onMoveUp,
  onMoveDown,
  onUpdateStep,
  onUpdateStepTranslation,
  onUserInputModeChange,
  onRemove,
  onPlay,
  onStop,
}: ScriptStepItemProps) => {
  const { t } = useTranslation('common');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isBaseLang = activeLanguage === effectiveBaseLanguage;

  const handleTextChange = (value: string) => {
    if (isBaseLang) {
      if (step.translations?.[effectiveBaseLanguage] !== undefined) {
        onUpdateStepTranslation(effectiveBaseLanguage, value);
      } else {
        onUpdateStep({ text: value });
      }
    } else {
      onUpdateStepTranslation(activeLanguage, value);
    }
  };

  return (
    <StepAccordion
      ref={containerRef}
      expanded={expanded}
      onChange={onToggleExpanded}
      disableGutters
      elevation={0}
    >
      <StepAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <ReorderButtonsBox onClick={(e) => e.stopPropagation()}>
          <IconButton
            size="small"
            onClick={onMoveUp}
            disabled={isFirst}
            sx={{ p: 0.25 }}
          >
            <PlayIcon sx={{ fontSize: 11, transform: 'rotate(-90deg)' }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={onMoveDown}
            disabled={isLast}
            sx={{ p: 0.25 }}
          >
            <PlayIcon sx={{ fontSize: 11, transform: 'rotate(90deg)' }} />
          </IconButton>
        </ReorderButtonsBox>

        <StepTitle>{t('assistant.scriptDialog.stepN', { n: index + 1 })}</StepTitle>

        <StepPreviewButton
          step={step}
          index={index}
          activeLanguage={activeLanguage}
          effectiveBaseLanguage={effectiveBaseLanguage}
          audioPlaybackKey={audioPlaybackKey}
          isAudioPlaying={isAudioPlaying}
          userFirstName={userFirstName}
          mapName={mapName}
          onPlay={onPlay}
          onStop={onStop}
        />
      </StepAccordionSummary>

      <StepAccordionDetails compactContent={!isBaseLang}>
        <StepContentBox>
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SectionLabelText>{t('assistant.scriptDialog.scriptText')}</SectionLabelText>
              <Tooltip
                title={
                  <Box
                    component="span"
                    sx={{ display: 'block', lineHeight: 2, fontSize: 13 }}
                  >
                    {t('assistant.scriptDialog.scriptTextHintPre')}{' '}
                    <strong>{'{{user}}'}</strong>{' '}
                    {t('assistant.scriptDialog.scriptTextHintUserPost')}
                    <br />
                    {t('assistant.scriptDialog.scriptTextHintPre')}{' '}
                    <strong>{'{{experience}}'}</strong>{' '}
                    {t('assistant.scriptDialog.scriptTextHintExpPost')}
                  </Box>
                }
                placement="top"
                arrow
              >
                <ScriptTextHintIcon />
              </Tooltip>
            </Box>

            {!isBaseLang && (
              <Box sx={{ mb: 1.5 }}>
                <BaseLangLabel>
                  {languageFlags[effectiveBaseLanguage]}{' '}
                  {t('assistant.scriptDialog.baseLang', {
                    language: languageLabels[effectiveBaseLanguage],
                  })}
                </BaseLangLabel>
                <BaseTextBox>
                  {step.text || (
                    <span style={{ opacity: 0.5 }}>
                      {t('assistant.scriptDialog.noBaseText')}
                    </span>
                  )}
                </BaseTextBox>
              </Box>
            )}

            <ScriptTextArea
              component="textarea"
              rows={isBaseLang ? 4 : 12}
              value={
                isBaseLang
                  ? step.translations?.[effectiveBaseLanguage] ?? step.text
                  : step.translations?.[activeLanguage] ?? ''
              }
              placeholder={
                isBaseLang
                  ? t('assistant.scriptDialog.enterScriptText')
                  : t('assistant.scriptDialog.enterTranslation', {
                      language: languageLabels[activeLanguage],
                    })
              }
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => handleTextChange(event.target.value)}
            />
          </Box>

          {isBaseLang && (
            <>
              {step.questionary?.type !== 'multiple_choice' && (
                <AdvanceToNextStepOptions
                  step={step}
                  onUpdateStep={onUpdateStep}
                />
              )}

              {step.questionary?.type !== 'multiple_choice' && (
                <UserInputControl
                  step={step}
                  stepUserInputMode={stepUserInputMode}
                  onModeChange={onUserInputModeChange}
                  onUpdateStep={onUpdateStep}
                />
              )}

              <QuestionarySection
                step={step}
                stepRef={containerRef.current}
                onUpdateStep={onUpdateStep}
              />
            </>
          )}

          <DeleteStepButton size="small" onClick={onRemove}>
            <DeleteIcon sx={{ fontSize: 18 }} />
          </DeleteStepButton>
        </StepContentBox>
      </StepAccordionDetails>
    </StepAccordion>
  );
};

export default ScriptStepItem;
